import _ from 'lodash';
import { ref, computed, onBeforeUnmount } from '@vue/composition-api';
import { ICSSObject } from '@/types';

export function useComponentReposition() {
  let component: HTMLElement;
  let container: HTMLElement | null | undefined;
  let isMoveEventAvailable: ((event: MouseEvent) => boolean) | undefined;

  let componentBox: DOMRect;
  let containerBox: DOMRect;

  const isMoving = ref(false);
  const isPinned = ref(false);
  const isCentered = ref(false);
  const moveStartPoint = ref({} as IPoint);
  const moveComponentBy = ref({} as IPoint);
  /* Component position relative to container element or browser window */
  const componentRelativePosition = ref({} as IPosition);

  function setComponentBox() {
    componentBox = component.getBoundingClientRect();
  }

  function setContainerBox() {
    containerBox = container
      ? container.getBoundingClientRect()
      : getWindowBox();
  }

  function handleMouseDown(event: MouseEvent) {
    const ignoreMoveTrigger = isPinned.value
      || (isMoveEventAvailable && !isMoveEventAvailable(event));
    if (ignoreMoveTrigger) {
      return;
    }

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    setComponentBox();
    setContainerBox();

    const top = componentBox.top - containerBox.top;
    const left = componentBox.left - containerBox.left;

    moveStartPoint.value = { x: event.x, y: event.y };
    componentRelativePosition.value = { top, left };
    isMoving.value = true;
  }

  function handleMouseMove(event: MouseEvent) {
    /* Ignore out of window bounds mouse move events */
    if (!isMoving.value || event.target === document) {
      return;
    }

    const deltaX = event.x - moveStartPoint.value.x!;
    const deltaY = event.y - moveStartPoint.value.y!;
    moveComponentBy.value = { x: deltaX, y: deltaY };
  }

  function handleMouseUp() {
    if (!isMoving.value) {
      return;
    }

    const box = component.getBoundingClientRect();
    const boxInWindow = alignComponentToContainerBox(box, containerBox);
    componentRelativePosition.value = {
      top: boxInWindow.top,
      left: boxInWindow.left,
    };

    isMoving.value = false;
    moveStartPoint.value = {};
    moveComponentBy.value = {};
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  }

  function setComponentRelativePosition({ top, left }: { top?: number; left?: number; }) {
    if (left) {
      componentRelativePosition.value = {
        ...componentRelativePosition.value,
        left,
      };
    }
    if (top) {
      componentRelativePosition.value = {
        ...componentRelativePosition.value,
        top,
      };
    }
  }

  function getWindowBox() {
    const height = window.innerHeight;
    const width = window.innerWidth;
    return { top: 0, left: 0, height, width } as DOMRect;
  }

  /** Top and left component sides have to be within container element */
  function alignComponentToContainerBox(box: DOMRect, container: DOMRect) {
    const top = box.top - container.top;
    const left = box.left - container.left;

    const minTopValue = Math.max(0, top);
    const minLeftValue = Math.max(0, left);
    const maxTopValue = container.height - box.height;
    const maxLeftValue = container.width - box.width;
    return {
      top: Math.min(minTopValue, maxTopValue),
      left: Math.min(minLeftValue, maxLeftValue),
    };
  }

  function applyReposition(options: {
    component: HTMLElement;
    /** Allow component repositioning only withing given container */
    container?: HTMLElement;
    /**
     * Use this to determine when "move" action is triggered
     * while interacting with component.
     */
    isMoveEventAvailable?(event: MouseEvent): boolean;
    isPinnedByDefault?: boolean;
    isCenteredByDefault?: boolean;
  }) {
    component = options.component;
    container = options.container;
    setContainerBox();
    isMoveEventAvailable = options.isMoveEventAvailable;
    isPinned.value = options.isPinnedByDefault ?? false;
    isCentered.value = options.isCenteredByDefault ?? false;

    component.addEventListener('mousedown', handleMouseDown);
  }

  function pin(value: boolean) {
    isPinned.value = value;

    if (isPinned.value) {
      isMoving.value = false;
      moveStartPoint.value = {};
      moveComponentBy.value = {};
      componentRelativePosition.value = {};
    }
  }

  const componentStyle = computed(() => {
    const style: ICSSObject = {};
    const position = container ? 'absolute' : 'fixed';

    /* Pinned item is centered on the screen by default */
    if (!isPinned.value && !isCentered.value) {
      style.position = position;
      style.top = '50%';
      style.left = '50%';
      style.marginRight = '-50%';
      style.transform = 'translate(-50%, -50%)';
    }

    /* NOTE: for header component FloatingBox */
    if (isCentered.value) {
      const componentBox = component.getBoundingClientRect();
      const top = containerBox.height / 2 - Math.min(componentBox.height, containerBox.height) / 2;
      const left = containerBox.width / 2 - Math.min(componentBox.width, containerBox.width) / 2;
      style.position = position;
      style.top = `${top}px`;
      style.left = `${left}px`;
      style.transform = 'none';
      style.bottom = 'auto';
      style.right = 'auto';
    }

    /* Component becomes detached when moved */
    if (!_.isEmpty(componentRelativePosition.value)) {
      style.position = position;
      style.top = `${componentRelativePosition.value.top}px`;
      style.left = `${componentRelativePosition.value.left}px`;
      style.bottom = 'auto';
      style.right = 'auto';
      style.marginRight = 'none';
      style.transform = 'none';
    }

    /* Use transform for performance while moving component around */
    if (!_.isEmpty(moveComponentBy.value)) {
      const translateX = `translateX(${moveComponentBy.value.x}px)`;
      const translateY = `translateY(${moveComponentBy.value.y}px)`;
      style.transform = `${translateX} ${translateY}`;
    }
    return style;
  });

  /* Auto clean up */
  onBeforeUnmount(() => {
    if (component) {
      component.removeEventListener('mousedown', handleMouseDown);
    }
  });

  return {
    applyReposition,
    pin,
    isPinned,
    componentStyle,
    setComponentRelativePosition,
  };
}

interface IPoint {
  readonly x?: number;
  readonly y?: number;
}

interface IPosition {
  readonly top?: number;
  readonly left?: number;
}
